import { Link } from 'react-router-dom'
import { Calendar, ArrowRight, ChevronRight } from 'lucide-react'
import { treatmentCategories, otherSpecialTreatments } from '../data'
import SEO from '../components/SEO'
import { imageUrl } from '../lib/images'

export default function Treatments() {
  // Funktion til at scrolle blødt til øvrige specialbehandlinger
  const scrollToSpecial = () => {
    const el = document.getElementById('oevrige-specialbehandlinger')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <div className="space-y-20 pb-20">
      <SEO
        title="Behandlinger | Rørbækklinikken i Viborg"
        description="Få overblik over vores 7 hovedområder: Kranio-sakral terapi, Specialbehandling af børn, Akupunktur, Muskelterapi & fysiurgisk massage, Bækken & ryg, Cupping & MFR samt Vejrtrækning & iltterapi."
      />
      
      {/* 2.1 Sidehoved & introduktion */}
      <section className="bg-[#F5F0E6]/50 py-16 sm:py-20 border-b border-[#E6DFD3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E352F]/5 text-[#1E352F] text-xs font-semibold tracking-wider uppercase">
            Behandlingsoversigt
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E352F]">
            Vores behandlingsområder
          </h1>
          <p className="text-base sm:text-lg text-[#1E352F]/85 leading-relaxed max-w-2xl mx-auto">
            På Rørbækklinikken arbejder vi helhedsorienteret med flere forskellige behandlingsformer. Herunder finder du vores 7 hovedområder og tilhørende specialer.
          </p>

          {/* 2.3 Knap til øvrige specialbehandlinger */}
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={scrollToSpecial}
              className="inline-flex items-center gap-2 bg-white hover:bg-[#F5F0E6] text-[#1E352F] border border-[#E6DFD3] px-6 py-3 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wider transition-colors shadow-xs cursor-pointer"
            >
              SE ØVRIGE SPECIALBEHANDLINGER
              <ArrowRight className="w-4 h-4 text-[#eebb53]" />
            </button>
            <a
              href="https://roerbaekklinikken.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#eebb53] hover:bg-[#dca63d] text-white px-6 py-3 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wider transition-colors shadow-sm"
            >
              <Calendar className="w-4 h-4" />
              Book tid online
            </a>
          </div>
        </div>
      </section>

      {/* Hurtig-oversigt over de 7 hovedområder */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        <div className="bg-white rounded-2xl border border-[#E6DFD3] p-4 sm:p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm">
            <span className="font-bold text-[#1E352F] mr-2">Gå direkte til:</span>
            {treatmentCategories.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                className="bg-[#FDFBF7] hover:bg-[#F5F0E6] text-[#1E352F]/85 hover:text-[#1E352F] border border-[#E6DFD3] px-3.5 py-1.5 rounded-full transition-colors font-medium"
              >
                {cat.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* 2.1 & 2.2 Hovedområder og underområder */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        {treatmentCategories.map((category, index) => (
          <div
            key={category.id}
            id={category.id}
            className="scroll-mt-24 bg-white rounded-3xl border border-[#E6DFD3] overflow-hidden shadow-xs"
          >
            {/* Hovedområde Header */}
            <div className="p-8 sm:p-10 lg:p-12 border-b border-[#E6DFD3] bg-[#FDFBF7]/60">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-8 space-y-3">
                  <div className="inline-flex items-center gap-2 text-xs font-bold text-[#eebb53] uppercase tracking-wider">
                    Hovedområde {index + 1} af 7
                  </div>
                  <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1E352F]">
                    {category.title}
                  </h2>
                  <p className="text-base text-[#1E352F]/80 leading-relaxed max-w-3xl">
                    {category.description}
                  </p>
                </div>

                {category.image && (
                  <div className="lg:col-span-4 flex justify-end">
                    <div className="w-full sm:w-80 h-44 rounded-2xl overflow-hidden border border-[#E6DFD3] shadow-xs">
                      <img
                        src={imageUrl(category.image, 'width=600,quality=85,format=auto')}
                        alt={category.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Underområder */}
            <div className="p-6 sm:p-10 bg-white space-y-4">
              <h3 className="font-serif text-sm font-bold text-[#1E352F]/70 uppercase tracking-wider mb-2">
                Underområder & specialer:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.subareas.map((sub, sIdx) => (
                  <div
                    key={sIdx}
                    className="bg-[#FDFBF7] rounded-2xl border border-[#E6DFD3] p-5 sm:p-6 flex flex-col justify-between hover:border-[#eebb53] hover:shadow-xs transition-all space-y-4"
                  >
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="font-serif text-lg font-bold text-[#1E352F]">
                          {sub.name}
                        </h4>
                        <span className="w-2 h-2 rounded-full bg-[#eebb53]" />
                      </div>
                      <p className="text-xs sm:text-sm text-[#1E352F]/75 leading-relaxed">
                        {sub.desc}
                      </p>
                    </div>

                    <div className="pt-2 flex items-center justify-between border-t border-[#E6DFD3]/60">
                      <Link
                        to={sub.link}
                        className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-[#1E352F] hover:text-[#eebb53] transition-colors"
                      >
                        Læs mere
                        <ChevronRight className="w-3.5 h-3.5" />
                      </Link>
                      <a
                        href="https://roerbaekklinikken.planway.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-semibold text-[#eebb53] hover:underline flex items-center gap-1"
                      >
                        <Calendar className="w-3 h-3" />
                        Bestil tid
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        ))}
      </section>

      {/* 2.3 Øvrige specialbehandlinger sektion */}
      <section id="oevrige-specialbehandlinger" className="scroll-mt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#F5F0E6] rounded-3xl border border-[#E6DFD3] p-8 sm:p-12 lg:p-14 space-y-8">
          <div className="max-w-3xl space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E352F]/10 text-[#1E352F] text-xs font-semibold tracking-wider uppercase">
              Yderligere tilbud
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1E352F]">
              Øvrige specialbehandlinger
            </h2>
            <p className="text-base text-[#1E352F]/80 leading-relaxed">
              Ud over de 7 primære hovedområder tilbyder vi en række specialiserede komplementære teknikker, der kan indgå som en naturlig del af dit behandlingsforløb.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {otherSpecialTreatments.map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl border border-[#E6DFD3] p-6 sm:p-7 space-y-4 flex flex-col justify-between shadow-xs"
              >
                <div className="space-y-3">
                  <h3 className="font-serif text-xl font-bold text-[#1E352F]">
                    {item.name}
                  </h3>
                  <p className="text-sm text-[#1E352F]/75 leading-relaxed">
                    {item.description}
                  </p>
                </div>

                <div className="pt-2 border-t border-[#E6DFD3]">
                  <Link
                    to={item.link}
                    className="inline-flex items-center gap-2 text-sm font-bold text-[#1E352F] hover:text-[#eebb53] transition-colors"
                  >
                    Læs om behandlingen
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Box */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#1E352F] rounded-3xl text-[#F5F0E6] p-8 sm:p-12 text-center space-y-6 border border-[#2D4A43]">
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
            Klar til at bestille din behandling?
          </h2>
          <p className="text-base text-[#F5F0E6]/80 max-w-2xl mx-auto leading-relaxed">
            Se alle ledige tider i vores online bookingsystem Planway og book din tid med det samme. Det er hurtigt, nemt og sikkert.
          </p>
          <div className="pt-2">
            <a
              href="https://roerbaekklinikken.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#eebb53] hover:bg-[#dca63d] text-white px-8 py-3.5 rounded-full text-base font-semibold transition-colors shadow-sm"
            >
              <Calendar className="w-5 h-5" />
              Book tid online
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
